import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Spin } from 'antd';
import { Link } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { LikeButton } from '../likeButton';
import ModalWindow from '../modalWindow';
import MaterialSelector from '../materialSelector';
import ImgFV from '../../assets/img/ImgFV.jpg';
import { ProductCardProps } from '../interfaces';
import { useProductCardData } from '../../hooks/useProductCardData';
import './productCard.scss';

const { Meta } = Card;

type ImageTransitionDirection = -1 | 0 | 1;

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  showMaterialSelector = true,
  showLikeButton = true,
}) => {
  const {
    modalVisible,
    setModalVisible,
    imageStatus,
    displayImage,
    imageIndex,
    setImageIndex,
    displayImages,
    handleMaterialSelect,
    materialIcons,
  } = useProductCardData(product, showMaterialSelector);
  const [imageReady, setImageReady] = useState(false);
  const [failedImageSrc, setFailedImageSrc] = useState<string | null>(null);
  const [canHoverPreview, setCanHoverPreview] = useState(false);
  const [imageTransitionDirection, setImageTransitionDirection] =
    useState<ImageTransitionDirection>(0);
  const hoverPreviewTimerRef = useRef<number | null>(null);
  const shouldUseFallback =
    Boolean(displayImage && failedImageSrc === displayImage) ||
    (!displayImage && (imageStatus === 'succeeded' || imageStatus === 'failed'));
  const resolvedDisplayImage = shouldUseFallback ? ImgFV : displayImage || '';
  const waitingForRemoteImage = !displayImage && !shouldUseFallback;
  const isMaterialImageLoading = imageStatus === 'loading' || waitingForRemoteImage;

  useEffect(() => {
    setImageReady(false);

    if (!resolvedDisplayImage) return;

    const preloadedImage = new Image();
    preloadedImage.src = resolvedDisplayImage;

    if (preloadedImage.complete) {
      setImageReady(true);
      return;
    }

    const handleLoad = () => setImageReady(true);
    const handleError = () => setImageReady(true);

    preloadedImage.onload = handleLoad;
    preloadedImage.onerror = handleError;

    return () => {
      preloadedImage.onload = null;
      preloadedImage.onerror = null;
    };
  }, [product.id, resolvedDisplayImage]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const updateHoverCapability = () => setCanHoverPreview(mediaQuery.matches);

    updateHoverCapability();
    mediaQuery.addEventListener('change', updateHoverCapability);

    return () => {
      mediaQuery.removeEventListener('change', updateHoverCapability);
    };
  }, []);

  useEffect(() => () => {
    if (hoverPreviewTimerRef.current !== null) {
      window.clearTimeout(hoverPreviewTimerRef.current);
    }
  }, []);

  const scheduleImageIndex = useCallback((
    nextIndex: number,
    delayMs: number,
    direction: ImageTransitionDirection,
  ) => {
    if (hoverPreviewTimerRef.current !== null) {
      window.clearTimeout(hoverPreviewTimerRef.current);
    }

    hoverPreviewTimerRef.current = window.setTimeout(() => {
      setImageTransitionDirection(direction);
      setImageIndex(nextIndex);
      hoverPreviewTimerRef.current = null;
    }, delayMs);
  }, [setImageIndex]);

  const handlePreviewMouseEnter = useCallback(() => {
    if (canHoverPreview && displayImages.length > 1) {
      scheduleImageIndex(1, 280, 1);
    }
  }, [canHoverPreview, displayImages.length, scheduleImageIndex]);

  const handlePreviewMouseLeave = useCallback(() => {
    if (canHoverPreview) {
      scheduleImageIndex(0, 320, -1);
    }
  }, [canHoverPreview, scheduleImageIndex]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (displayImages.length > 1) {
        setImageTransitionDirection(1);
        setImageIndex((prev) => (prev + 1) % displayImages.length);
      }
    },
    onSwipedRight: () => {
      if (displayImages.length > 1) {
        setImageTransitionDirection(-1);
        setImageIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
      }
    },
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.onerror = null;
    setFailedImageSrc(displayImage || e.currentTarget.currentSrc || e.currentTarget.src);
    setImageReady(true);
  };

  const handleMaterialPreviewSelect = useCallback((materialName: string) => {
    setImageTransitionDirection(0);
    handleMaterialSelect(materialName);
  }, [handleMaterialSelect]);

  const imageTransitionClass =
    imageTransitionDirection === 1
      ? 'slide-forward'
      : imageTransitionDirection === -1
        ? 'slide-back'
        : '';

  return (
    <div className="card-wrapper">
      <Card hoverable className="my-product-card">
        <div className="card-horizontal">
          <Link to={`/products/${product.id}`} className="card-link">
            <div
              className="image-container"
              {...swipeHandlers}
              onMouseEnter={handlePreviewMouseEnter}
              onMouseLeave={handlePreviewMouseLeave}
            >
              {(isMaterialImageLoading || !imageReady) && (
                <div className="spinner-container" aria-label="Loading material image">
                  <Spin size="large" />
                </div>
              )}
              {resolvedDisplayImage && (
                <img
                  key={`${resolvedDisplayImage}-${imageTransitionDirection}`}
                  src={resolvedDisplayImage}
                  alt={product.title}
                  onError={handleImageError}
                  className={`card-image ${imageTransitionClass} ${
                    imageReady && !waitingForRemoteImage ? 'is-visible' : 'is-hidden'
                  } ${shouldUseFallback ? 'is-fallback' : ''}`}
                />
              )}
            </div>
          </Link>
          <div className="card-details">
            <div
              className="card-image-dots"
              aria-label={displayImages.length > 1 ? `Product gallery: ${imageIndex + 1} of ${displayImages.length}` : undefined}
            >
              {displayImages.length > 1 && displayImages.map((image, index) => (
                <span
                  key={`${product.id}-dot-${image}-${index}`}
                  className={`card-image-dot ${index === imageIndex ? 'is-active' : ''}`}
                />
              ))}
            </div>
            <Meta title={product.title} />
            {showMaterialSelector && (
              <div className="preview-images-wrapper">
                <MaterialSelector
                  productId={product.id}
                  dataSource="product"
                  materials={materialIcons}
                  value={product.selectedMaterial}
                  onSelect={handleMaterialPreviewSelect}
                />
              </div>
            )}
          </div>
        </div>
      </Card>
      {showLikeButton && (
        <>
          <div className="heart-icon">
            <LikeButton
              productId={product.id}
              iconSize={20}
              onAuthRequired={() => setModalVisible(true)}
            />
          </div>
          <ModalWindow visible={modalVisible} onCancel={() => setModalVisible(false)} />
        </>
      )}
    </div>
  );
};

export default ProductCard;
